import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import MainCard from 'components/MainCard';
import { createSubstore } from 'api/substoreCreate';
import { deleteSubstore } from 'api/substore';
import { useSubStores } from 'contexts/SubStoreContext';


const defaultSubStore = {
  name: '',
  address: '',
  working_time_start: '09:00',
  working_time_end: '17:00'
};

export default function SubStores() {
  const navigate = useNavigate();
  const { substores, addSubStore, removeSubStore } = useSubStores();
  const [search, setSearch] = useState('');
  const [searchResult, setSearchResult] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [removeIdx, setRemoveIdx] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [newSubStore, setNewSubStore] = useState(defaultSubStore);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Search handler
  const handleSearch = () => {
    const safeSearch = (search || '').trim();
    setSearchResult((substores || []).filter((s) => s.name.toLowerCase().includes(safeSearch.toLowerCase())));
  };

  // Add substore dialog handlers (frontend only, does not persist to backend)
  const openAddDialog = () => {
    setNewSubStore(defaultSubStore);
    setAddOpen(true);
  };
  const closeAddDialog = () => setAddOpen(false);
  const handleAddSubStore = async () => {
    if (!(newSubStore.name || '').trim() || !(newSubStore.address || '').trim()) return;
    try {
      const payload = {
        name: (newSubStore.name || '').trim(),
        address: (newSubStore.address || '').trim(),
        working_time_start: (newSubStore.working_time_start || '').trim(),
        working_time_end: (newSubStore.working_time_end || '').trim()
      };
      const created = await createSubstore(payload);
      addSubStore(created);
      setAddOpen(false);
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to create substore.';
      showErrorPopup(errorMessage);
    }
  };

  // Remove substore dialog handlers (frontend only, does not persist to backend)
  
  const openRemoveDialog = (idx, id) => {
    setRemoveIdx(id);
    setConfirmOpen(true);
  };
  const closeRemoveDialog = () => setConfirmOpen(false);
  
  const showErrorPopup = (message) => {
    setErrorMessage(message);
    setErrorDialogOpen(true);
  };
  
  const closeErrorDialog = () => {
    setErrorDialogOpen(false);
    setErrorMessage('');
  };
  const handleRemoveSubstore = async () => {
    if (!removeIdx) return;
    try {
      await deleteSubstore(removeIdx);
      // Find the index of the substore to remove
      const substoreIndex = substores.findIndex(sub => sub.id === removeIdx);
      if (substoreIndex !== -1) {
        removeSubStore(substoreIndex);
      }
      setSearchResult(prev => prev.filter(sub => sub.id !== removeIdx));
      setConfirmOpen(false);
      setRemoveIdx(null);
    } catch (err) {
      showErrorPopup(err.message || 'Failed to delete substore.');
      setConfirmOpen(false);
      setRemoveIdx(null);
    }
  };
  

  // Which list to show - ensure it's always an array
  const listToShow = React.useMemo(() => {
    try {
      if ((search || '').trim()) {
        const result = Array.isArray(searchResult) ? searchResult : [];
        console.log('Using searchResult:', result);
        return result;
      }
      const result = Array.isArray(substores) ? substores : [];
      console.log('Using substores:', result);
      return result;
    } catch (error) {
      console.error('Error in listToShow useMemo:', error);
      return [];
    }
  }, [search, searchResult, substores]);

  // Debug logging
  console.log('SubStores Page - All substores:', substores?.length || 0);
  console.log('SubStores Page - List to show:', listToShow?.length || 0);
  console.log('SubStores Page - listToShow type:', typeof listToShow);
  console.log('SubStores Page - listToShow isArray:', Array.isArray(listToShow));
  console.log('SubStores Page - listToShow value:', listToShow);
  console.log('SubStores Page - Token exists:', !!localStorage.getItem('token'));
  console.log('SubStores Page - localStorage substores:', localStorage.getItem('substores'));

  const handleSubstoreClick = (store) => {
    // Navigate using the actual substore ID from the backend
    navigate(`/substore/${store.id}`);
  };

  // Manual refresh function
  const handleRefresh = async () => {
    try {
      console.log('Manually refreshing substores...');
      const { cacheAfterLogin } = await import('api/cacheAfterLogin');
      const result = await cacheAfterLogin();
      console.log('Refresh result:', result);
      // The context should automatically pick up the updated localStorage data
      window.location.reload(); // Simple refresh to reload the context
    } catch (error) {
      console.error('Error refreshing substores:', error);
      showErrorPopup('Failed to refresh substores');
    }
  };

  return (
    <MainCard title="">
      <Box display="flex" alignItems="center" justifyContent="flex-end" mb={2}>
        <Button variant="contained" color="primary" onClick={openAddDialog}>
          Add Sub Store
        </Button>
      </Box>
      <Typography variant="subtitle1" gutterBottom>
        Sub Store List
      </Typography>
      <List>
        {Array.isArray(listToShow) && listToShow.length === 0 && (
          <ListItem>
            <Typography color="text.secondary">No sub stores found.</Typography>
          </ListItem>
        )}
        {Array.isArray(listToShow) && listToShow.map((store, idx) => (
          <ListItem
            key={store.id || idx}
            onClick={() => handleSubstoreClick(store)}
            sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
            secondaryAction={
              <IconButton
                edge="end"
                aria-label="delete"
                onClick={(e) => {
                  e.stopPropagation();
                  openRemoveDialog(idx, store.id);
                }}
              >
              <DeleteIcon />
              </IconButton>
            }
          >
            <Box>
              <Typography fontWeight={600} color="primary">
                {store.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {store.address}
              </Typography>
            </Box>
          </ListItem>
        ))}
      </List>

      {/* Add Sub Store Dialog */}
      <Dialog open={addOpen} onClose={closeAddDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Add Sub Store</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Store Name"
              value={newSubStore.name}
              onChange={(e) => setNewSubStore((s) => ({ ...s, name: e.target.value }))}
              required
            />
            <TextField
              label="Address"
              value={newSubStore.address}
              onChange={(e) => setNewSubStore((s) => ({ ...s, address: e.target.value }))}
              multiline
              minRows={2}
              required
            />
            <Box display="flex" gap={2}>
              <TextField
                label="Working Hours Start"
                type="time"
                value={newSubStore.working_time_start}
                onChange={(e) => setNewSubStore((s) => ({ ...s, working_time_start: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}
                fullWidth
              />
              <TextField
                label="Working Hours End"
                type="time"
                value={newSubStore.working_time_end}
                onChange={(e) => setNewSubStore((s) => ({ ...s, working_time_end: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}
                fullWidth
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAddDialog}>Cancel</Button>
          <Button onClick={handleAddSubStore} variant="contained" disabled={!(newSubStore.name || '').trim() || !(newSubStore.address || '').trim()}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={closeRemoveDialog} maxWidth="xs">
        <DialogTitle>Remove Sub Store</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to remove this sub store?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRemoveDialog}>Cancel</Button>
          <Button onClick={handleRemoveSubstore} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Dialog */}
      <Dialog open={errorDialogOpen} onClose={closeErrorDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Error</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mt: 1 }}>
            <AlertTitle>Operation Failed</AlertTitle>
            {errorMessage}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeErrorDialog} variant="contained" color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </MainCard>
  );
}
